import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, MapPin, Calendar, Users, Star, Heart, ChevronRight, ChevronLeft,
  Home, Building2, Tent, TreePine, Waves, Mountain, Loader2,
  Filter, X, Download, Smartphone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Property type icons
const propertyTypeIcons = {
  'House': Home,
  'Apartment': Building2,
  'Villa': Building2,
  'Cabin': TreePine,
  'Tent': Tent,
  'Beach House': Waves,
  'Chalet': Mountain,
};

// Property Card Component
const PropertyCard = ({ property, onFavorite, searchDates }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const navigate = useNavigate();
  
  const mainImage = property.photos?.[0]?.url || property.image_url || 
    'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&auto=format&fit=crop&q=60';
  
  const handleClick = () => {
    // Build URL with search dates if available
    let url = `/marketplace/property/${property.property_id}`;
    if (searchDates?.checkIn || searchDates?.checkOut || searchDates?.guests) {
      const params = new URLSearchParams();
      if (searchDates.checkIn) params.set('checkIn', searchDates.checkIn);
      if (searchDates.checkOut) params.set('checkOut', searchDates.checkOut);
      if (searchDates.guests) params.set('guests', searchDates.guests);
      url += `?${params.toString()}`;
    }
    navigate(url);
  };
  
  return (
    <div 
      className="group cursor-pointer"
      onClick={handleClick}
      data-testid={`property-card-${property.property_id}`}
    >
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-3">
        <img 
          src={mainImage} 
          alt={property.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsFavorite(!isFavorite);
            onFavorite?.(property.property_id, !isFavorite);
          }}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
        </button>
        {property.is_featured && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full">
            Featured
          </div>
        )}
        {property.instant_booking && (
          <div className="absolute bottom-3 left-3 px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
            Instant Book
          </div>
        )}
      </div>
      
      <div className="space-y-1">
        <div className="flex items-start justify-between">
          <h3 className="font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
          {property.average_rating > 0 && (
            <div className="flex items-center gap-1 text-sm">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="font-medium">{property.average_rating?.toFixed(1)}</span>
            </div>
          )}
        </div>
        <p className="text-gray-500 text-sm flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" />
          {property.city}{property.country ? `, ${property.country}` : ''}
        </p>
        <p className="text-gray-500 text-sm">
          {property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''} · {property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''} · Up to {property.max_guests} guests
        </p>
        <p className="font-semibold text-gray-900 pt-1">
          {property.currency || 'KES'} {property.base_price?.toLocaleString()}
          <span className="font-normal text-gray-500"> / night</span>
        </p>
      </div>
    </div>
  );
};

// Date Range Picker Component (Airbnb-style)
const DateRangePicker = ({ checkIn, checkOut, onDateChange, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingEnd, setSelectingEnd] = useState(!checkIn);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const parseDate = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00') : null;
  const formatDate = (date) => date ? date.toISOString().split('T')[0] : '';
  
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  
  const startDate = parseDate(checkIn);
  const endDate = parseDate(checkOut);
  
  const isSelected = (date) => {
    if (!startDate) return false;
    return date.getTime() === startDate.getTime() || (endDate && date.getTime() === endDate.getTime());
  };
  
  const isInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date > startDate && date < endDate;
  };
  
  const handleDateClick = (date) => {
    if (isPastDate(date)) return;
    
    if (!startDate || (startDate && endDate)) {
      onDateChange(formatDate(date), '');
      setSelectingEnd(true);
    } else if (selectingEnd) {
      if (date > startDate) {
        onDateChange(checkIn, formatDate(date));
        setSelectingEnd(false);
      } else {
        onDateChange(formatDate(date), '');
      }
    }
  };
  
  const renderMonth = (monthOffset = 0) => {
    const month = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(month.getFullYear(), month.getMonth());
    const firstDay = getFirstDayOfMonth(month.getFullYear(), month.getMonth());
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${monthOffset}-${i}`} className="w-10 h-10 md:w-9 md:h-9" />);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const isPast = isPastDate(date);
      const selected = isSelected(date);
      const inRange = isInRange(date);
      const isStart = startDate && date.getTime() === startDate.getTime();
      const isEnd = endDate && date.getTime() === endDate.getTime();
      
      days.push(
        <button
          key={`${monthOffset}-${day}`}
          onClick={() => handleDateClick(date)}
          disabled={isPast}
          className={`w-10 h-10 md:w-9 md:h-9 rounded-full text-sm font-medium transition-all
            ${isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 hover:scale-110'}
            ${selected ? 'bg-gray-900 text-white hover:bg-gray-800' : ''}
            ${inRange ? 'bg-gray-100' : ''}
            ${isStart && endDate ? 'rounded-r-none' : ''}
            ${isEnd ? 'rounded-l-none' : ''}
          `}
        >
          {day}
        </button>
      );
    }
    
    return (
      <div className="flex-1 min-w-[280px]">
        <div className="text-center font-semibold text-gray-900 mb-4 text-sm md:text-base">
          {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-xs text-gray-500 mb-2">
          <div>Su</div><div>Mo</div><div>Tu</div><div>We</div><div>Th</div><div>Fr</div><div>Sa</div>
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {days}
        </div>
      </div>
    );
  };
  
  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 md:p-6 w-full max-w-[640px]" data-testid="date-range-picker">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-sm font-medium text-gray-600">
          {selectingEnd && startDate ? 'Select checkout date' : 'Select check-in date'}
        </span>
        <button 
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      {/* Calendar Grid - 2 months on desktop, 1 on mobile */}
      <div className={`flex ${isMobile ? 'flex-col gap-6' : 'gap-8'}`}>
        {renderMonth(0)}
        {renderMonth(1)}
      </div>
      
      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
        <button 
          onClick={() => {
            onDateChange('', '');
            setSelectingEnd(true);
          }}
          className="text-sm font-medium text-gray-700 hover:underline"
        >
          Clear dates
        </button>
        <div className="flex gap-2">
          {startDate && endDate && (
            <span className="text-sm text-gray-500">
              {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} nights
            </span>
          )}
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// Search Bar Component with Date Range Picker
const SearchBar = ({ onSearch, initialValues = {} }) => {
  const [location, setLocation] = useState(initialValues.location || '');
  const [checkIn, setCheckIn] = useState(initialValues.checkIn || '');
  const [checkOut, setCheckOut] = useState(initialValues.checkOut || '');
  const [guests, setGuests] = useState(initialValues.guests || '2');
  const [showCalendar, setShowCalendar] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => setIsMobileView(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const handleSearch = () => {
    onSearch({ location, checkIn, checkOut, guests: guests ? parseInt(guests) : null });
  };
  
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return 'Add date';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Render both mobile and desktop, use CSS to show/hide
  return (
    <>
      {/* Mobile Search Bar - shows on screens < 768px */}
      <div className="md:hidden">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 mx-4">
          <div className="space-y-3">
            {/* Location */}
            <div className="pb-3 border-b border-gray-100">
              <label className="block text-xs font-semibold text-gray-800 mb-1">Where</label>
              <Input
                type="text"
                placeholder="Search destinations"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="border-0 p-0 h-auto text-base focus-visible:ring-0 placeholder:text-gray-400"
                data-testid="search-location-mobile"
              />
            </div>
            
            {/* Dates */}
            <div 
              className="pb-3 border-b border-gray-100 cursor-pointer"
              onClick={() => setShowCalendar(true)}
            >
              <label className="block text-xs font-semibold text-gray-800 mb-1">When</label>
              <div className="flex items-center gap-2 text-base">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className={checkIn ? 'text-gray-900' : 'text-gray-400'}>
                  {checkIn ? `${formatDisplayDate(checkIn)} - ${formatDisplayDate(checkOut)}` : 'Add dates'}
                </span>
              </div>
            </div>
            
            {/* Guests */}
            <div className="pb-3">
              <label className="block text-xs font-semibold text-gray-800 mb-1">Guests</label>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <select
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="flex-1 text-base bg-transparent border-0 focus:ring-0 p-0"
                  data-testid="search-guests-mobile"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <option key={n} value={n}>{n} guest{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Search Button */}
            <Button 
              onClick={handleSearch}
              className="w-full rounded-xl bg-[#FF385C] hover:bg-[#E31C5F] text-white h-12 text-base font-semibold"
              data-testid="search-btn-mobile"
            >
              <Search className="w-5 h-5 mr-2" /> Search
            </Button>
          </div>
        </div>
      </div>
      
      {/* Desktop Search Bar - shows on screens >= 768px */}
      <div className="hidden md:block">
        <div className="bg-white rounded-full shadow-lg border border-gray-200 p-2 flex items-center gap-2 max-w-4xl mx-auto relative">
          <div className="flex-1 px-4 py-2 border-r border-gray-200">
            <label className="block text-xs font-semibold text-gray-800">Where</label>
            <Input
              type="text"
              placeholder="Search destinations"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="border-0 p-0 h-auto text-sm focus-visible:ring-0 placeholder:text-gray-400"
              data-testid="search-location"
            />
          </div>
          <div 
            className="px-4 py-2 border-r border-gray-200 cursor-pointer hover:bg-gray-50 rounded-full transition-colors"
            onClick={() => setShowCalendar(true)}
          >
            <label className="block text-xs font-semibold text-gray-800">Check in</label>
            <div className="text-sm" data-testid="search-checkin">
              {formatDisplayDate(checkIn)}
            </div>
          </div>
          <div 
            className="px-4 py-2 border-r border-gray-200 cursor-pointer hover:bg-gray-50 rounded-full transition-colors"
            onClick={() => setShowCalendar(true)}
          >
            <label className="block text-xs font-semibold text-gray-800">Check out</label>
            <div className="text-sm" data-testid="search-checkout">
              {formatDisplayDate(checkOut)}
            </div>
          </div>
          <div className="px-4 py-2">
            <label className="block text-xs font-semibold text-gray-800">Guests</label>
            <select
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              className="text-sm bg-transparent border-0 focus:ring-0 p-0 cursor-pointer"
              data-testid="search-guests"
            >
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <option key={n} value={n}>{n} guest{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
          <Button 
            onClick={handleSearch}
            className="rounded-full bg-[#FF385C] hover:bg-[#E31C5F] text-white px-6 h-12"
            data-testid="search-btn"
          >
            <Search className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Calendar Modal - Works for both mobile and desktop */}
      {showCalendar && (
        <>
          {/* Mobile Modal */}
          <div className="md:hidden fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
            <div className="bg-white w-full max-h-[85vh] overflow-y-auto rounded-t-3xl">
              <div className="sticky top-0 bg-white p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Select dates</h3>
                <button onClick={() => setShowCalendar(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <DateRangePicker 
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDateChange={(start, end) => {
                    setCheckIn(start);
                    setCheckOut(end);
                  }}
                  onClose={() => setShowCalendar(false)}
                />
              </div>
            </div>
          </div>
          
          {/* Desktop Dropdown */}
          <div className="hidden md:block">
            <div className="fixed inset-0 z-40" onClick={() => setShowCalendar(false)} />
            <div className="absolute left-1/2 -translate-x-1/2 mt-4 z-50">
              <DateRangePicker 
                checkIn={checkIn}
                checkOut={checkOut}
                onDateChange={(start, end) => {
                  setCheckIn(start);
                  setCheckOut(end);
                }}
                onClose={() => setShowCalendar(false)}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Category Pills
const CategoryPills = ({ categories, selected, onSelect }) => {
  return (
    <div className="flex items-center gap-8 overflow-x-auto pb-4 scrollbar-hide">
      {categories.map((cat) => {
        const Icon = propertyTypeIcons[cat] || Home;
        const isSelected = selected === cat;
        return (
          <button
            key={cat}
            onClick={() => onSelect(isSelected ? null : cat)}
            className={`flex flex-col items-center gap-2 min-w-fit pb-2 border-b-2 transition-colors ${
              isSelected 
                ? 'border-gray-900 text-gray-900' 
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <Icon className="w-6 h-6" />
            <span className="text-xs font-medium whitespace-nowrap">{cat}</span>
          </button>
        );
      })}
    </div>
  );
};

// Main Marketplace Page
export default function MarketplacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [settings, setSettings] = useState(null);
  const [properties, setProperties] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('type') || null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // PWA Install State
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  // Register PWA service worker and handle install prompt
  useEffect(() => {
    // Check if running as standalone PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone || 
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/marketplace-sw.js')
        .then((reg) => {
          console.log('iStayBnB PWA: Service worker registered', reg.scope);
        })
        .catch((err) => console.error('SW registration failed:', err));
    }
    
    // Update manifest link for marketplace
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/marketplace-manifest.json';
    } else {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/marketplace-manifest.json';
      document.head.appendChild(link);
    }
    
    // Update theme color
    let themeMetaTag = document.querySelector('meta[name="theme-color"]');
    if (themeMetaTag) {
      themeMetaTag.content = '#FF385C';
    }
    
    // Update page title
    document.title = 'iStayBnB - Book Unique Stays';
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install banner after 5 seconds if not in standalone mode
      if (!standalone) {
        setTimeout(() => setShowInstallBanner(true), 5000);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);
  
  // Handle PWA install
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the PWA install');
    }
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };
  
  // Load marketplace settings
  const loadSettings = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/settings`);
      if (!resp.ok) throw new Error('iStayBnB unavailable');
      const data = await resp.json();
      setSettings(data);
      return data.enabled;
    } catch (err) {
      setError('iStayBnB is currently unavailable');
      return false;
    }
  }, []);
  
  // Load filters
  const loadFilters = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/filters`);
      if (resp.ok) {
        const data = await resp.json();
        setFilters(data);
      }
    } catch {}
  }, []);
  
  // Load featured properties
  const loadFeatured = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/properties/featured?limit=6`);
      if (resp.ok) {
        const data = await resp.json();
        setFeaturedProperties(data.properties || []);
      }
    } catch {}
  }, []);
  
  // Load properties with search params
  const loadProperties = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.location) queryParams.set('location', params.location);
      if (params.checkIn) queryParams.set('check_in', params.checkIn);
      if (params.checkOut) queryParams.set('check_out', params.checkOut);
      if (params.guests) queryParams.set('guests', params.guests);
      if (selectedCategory) queryParams.set('property_type', selectedCategory);
      queryParams.set('page', params.page || currentPage);
      queryParams.set('limit', '20');
      
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/properties?${queryParams}`);
      if (!resp.ok) throw new Error('Failed to load properties');
      
      const data = await resp.json();
      setProperties(data.properties || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Load properties error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory]);
  
  // Initial load
  useEffect(() => {
    const init = async () => {
      const enabled = await loadSettings();
      if (enabled) {
        await Promise.all([loadFilters(), loadFeatured(), loadProperties()]);
      }
      setLoading(false);
    };
    init();
  }, [loadSettings, loadFilters, loadFeatured, loadProperties]);
  
  // Reload when category changes
  useEffect(() => {
    if (settings?.enabled) {
      loadProperties();
    }
  }, [selectedCategory, settings?.enabled, loadProperties]);
  
  const handleSearch = (params) => {
    setCurrentPage(1);
    loadProperties({ ...params, page: 1 });
    // Update URL
    const newParams = new URLSearchParams();
    if (params.location) newParams.set('location', params.location);
    if (params.checkIn) newParams.set('checkIn', params.checkIn);
    if (params.checkOut) newParams.set('checkOut', params.checkOut);
    if (params.guests) newParams.set('guests', params.guests);
    setSearchParams(newParams);
  };
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">iStayBnB Unavailable</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white" data-testid="marketplace-page">
      {/* PWA Install Banner */}
      {showInstallBanner && deferredPrompt && !isStandalone && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-[#FF385C] to-[#E31C5F] text-white safe-area-inset-bottom" data-testid="pwa-install-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base">Install iStayBnB App</p>
                <p className="text-xs sm:text-sm text-white/80">Book stays faster with our app</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowInstallBanner(false)}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                Not now
              </Button>
              <Button 
                onClick={handleInstallClick}
                className="bg-white text-[#FF385C] hover:bg-white/90 rounded-full px-4 h-9 text-sm font-semibold"
                data-testid="pwa-install-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Install
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/marketplace')}>
              <div className="w-10 h-10 bg-gradient-to-br from-[#FF385C] to-[#E31C5F] rounded-xl flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">{settings?.name || 'iStayBnB'}</span>
            </div>
            
            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="/marketplace" className="text-sm font-medium text-gray-900">Stays</a>
              <a href="#" className="text-sm font-medium text-gray-500 hover:text-gray-900">Experiences</a>
            </nav>
            
            {/* Actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* PWA Install Button (when banner is dismissed) */}
              {deferredPrompt && !showInstallBanner && !isStandalone && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleInstallClick}
                  className="hidden sm:flex items-center gap-1.5 rounded-full border-[#FF385C] text-[#FF385C] hover:bg-[#FF385C]/5"
                  data-testid="header-install-btn"
                >
                  <Download className="w-4 h-4" />
                  Get App
                </Button>
              )}
              <Button 
                variant="ghost" 
                className="text-sm font-medium hidden sm:inline-flex"
                onClick={() => navigate('/login')}
              >
                List your property
              </Button>
              <Button 
                onClick={() => navigate('/marketplace/login')}
                className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-full px-4 sm:px-6 text-sm"
                data-testid="guest-signin-btn"
              >
                Sign in
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#FF385C]/5 via-white to-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {settings?.tagline || 'Find your perfect stay'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover unique homes and experiences around the world
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <SearchBar 
              onSearch={handleSearch}
              initialValues={{
                location: searchParams.get('location') || '',
                checkIn: searchParams.get('checkIn') || '',
                checkOut: searchParams.get('checkOut') || '',
                guests: searchParams.get('guests') || ''
              }}
            />
          </div>
        </div>
      </section>
      
      {/* Category Pills */}
      {filters?.property_types?.length > 0 && (
        <section className="border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <CategoryPills 
              categories={filters.property_types}
              selected={selectedCategory}
              onSelect={(cat) => {
                setSelectedCategory(cat);
                setCurrentPage(1);
              }}
            />
          </div>
        </section>
      )}
      
      {/* Featured Properties */}
      {featuredProperties.length > 0 && !searchParams.get('location') && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Featured Stays</h2>
              <Button variant="ghost" className="text-[#FF385C] hover:text-[#E31C5F]">
                View all <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProperties.slice(0, 6).map((prop) => (
                <PropertyCard key={prop.property_id} property={prop} />
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* All Properties */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {searchParams.get('checkIn') && searchParams.get('checkOut') 
                  ? 'Available stays for your dates'
                  : selectedCategory ? `${selectedCategory}s` : 'All Stays'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {properties.length} properties available
                {searchParams.get('checkIn') && searchParams.get('checkOut') && (
                  <span className="text-[#FF385C] ml-2">
                    for {new Date(searchParams.get('checkIn')).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})} 
                    {' - '} 
                    {new Date(searchParams.get('checkOut')).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                  </span>
                )}
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF385C]" />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-20">
              <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No properties found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {properties.map((prop) => (
                  <PropertyCard 
                    key={prop.property_id} 
                    property={prop} 
                    searchDates={{
                      checkIn: searchParams.get('checkIn'),
                      checkOut: searchParams.get('checkOut'),
                      guests: searchParams.get('guests')
                    }}
                  />
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-4 text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-gray-900">Help Center</a></li>
                <li><a href="#" className="hover:text-gray-900">Safety information</a></li>
                <li><a href="#" className="hover:text-gray-900">Cancellation options</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Hosting</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/" className="hover:text-gray-900">List your property</a></li>
                <li><a href="#" className="hover:text-gray-900">Host resources</a></li>
                <li><a href="#" className="hover:text-gray-900">Community forum</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">{settings?.name || 'iStayBnB'}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-gray-900">About us</a></li>
                <li><a href="#" className="hover:text-gray-900">Careers</a></li>
                <li><a href="#" className="hover:text-gray-900">Newsroom</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Get the App</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <button 
                    onClick={() => {
                      if (window.deferredPrompt) {
                        window.deferredPrompt.prompt();
                        window.deferredPrompt.userChoice.then((choiceResult) => {
                          window.deferredPrompt = null;
                        });
                      } else if (navigator.standalone === false || window.matchMedia('(display-mode: browser)').matches) {
                        alert('To install: tap the share button and select "Add to Home Screen"');
                      } else {
                        alert('App is already installed or installation is not supported');
                      }
                    }}
                    className="hover:text-gray-900 flex items-center gap-2"
                    data-testid="install-pwa-footer"
                  >
                    <Download className="w-4 h-4" />
                    Install iStayBnB App
                  </button>
                </li>
                <li>
                  <a href="https://expo.dev/artifacts/eas/mwkRuexrysrgRydZ6srQJ5.apk" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="hover:text-gray-900 flex items-center gap-2"
                  >
                    <Smartphone className="w-4 h-4" />
                    Download Android APK
                  </a>
                </li>
              </ul>
              {settings?.support_email && (
                <p className="text-sm text-gray-600 mt-4">{settings.support_email}</p>
              )}
              {settings?.support_phone && (
                <p className="text-sm text-gray-600">{settings.support_phone}</p>
              )}
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {settings?.name || 'iStayBnB'}. All rights reserved.
            </p>
            <div className="flex items-center gap-4 mt-4 md:mt-0">
              <a href={settings?.terms_url || '#'} className="text-sm text-gray-500 hover:text-gray-900">Terms</a>
              <a href={settings?.privacy_url || '#'} className="text-sm text-gray-500 hover:text-gray-900">Privacy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
